import { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from './supabase'

const AuthContext = createContext({})

export function AuthProvider({ children }) {
  const [user, setUser]             = useState(null)
  const [boutique, setBoutique]     = useState(null)
  const [role, setRole]             = useState('patron')
  const [boutiqueId, setBoutiqueId] = useState(null)
  const [loading, setLoading]       = useState(true)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null)
      if (session?.user) setupUser(session.user)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setUser(session?.user ?? null)
        if (session?.user) setupUser(session.user)
        else {
          setBoutique(null)
          setRole('patron')
          setBoutiqueId(null)
          setLoading(false)
        }
      }
    )
    return () => subscription.unsubscribe()
  }, [])

  async function setupUser(user) {
    try {
      // Appelle la fonction SQL SECURITY DEFINER
      // qui bypass la RLS pour lire la table employes
      const { data: empRows, error: empError } = await supabase
        .rpc('get_boutique_par_email', { user_email: user.email })

      const empInfo = empRows?.[0]

      if (empInfo && empInfo.boutique_id && empInfo.boutique_id !== user.id) {
        // ── EMPLOYÉ : charge la boutique du patron ──────────
        setRole(empInfo.employe_role || 'caissier')
        setBoutiqueId(empInfo.boutique_id)

        const { data: boutiquePatron } = await supabase
          .from('boutiques')
          .select('*')
          .eq('id', empInfo.boutique_id)
          .single()

        setBoutique(boutiquePatron)
      } else {
        // ── PATRON : charge sa propre boutique ──────────────
        setRole('patron')
        setBoutiqueId(user.id)

        const { data: maBoutique } = await supabase
          .from('boutiques')
          .select('*')
          .eq('id', user.id)
          .single()

        setBoutique(maBoutique)
      }
    } catch (err) {
      console.error('setupUser error:', err)
      // En cas d'erreur, on traite comme patron
      setRole('patron')
      setBoutiqueId(user.id)
    } finally {
      setLoading(false)
    }
  }

  async function signUp({ email, password, nom, telephone }) {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { nom, telephone } }
    })
    return { data, error }
  }

  async function signIn({ email, password }) {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password })
    return { data, error }
  }

  async function signInWithGoogle() {
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: { redirectTo: window.location.origin }
    })
    return { data, error }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  return (
    <AuthContext.Provider value={{
      user, boutique, role, boutiqueId, loading,
      isPatron: role === 'patron',
      isCaissier: role === 'caissier',
      signUp, signIn, signInWithGoogle, signOut
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
